// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Star, MessageCircle, Calendar, User, Trophy, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

interface GraphicsVendorReview {
  id: string;
  vendor_id: string;
  reviewer_id: string;
  order_id?: string;
  rating: number;
  review_text?: string;
  created_at: string;
  updated_at: string;
  profiles?: {
    name?: string;
    email?: string;
  };
  graphics_orders?: {
    total_amount?: number;
    graphics_packages?: {
      name?: string;
    };
  };
}

interface ReviewStats {
  total_reviews: number;
  average_rating: number;
  rating_distribution: {
    [key: number]: number;
  };
  recent_reviews: number;
}

const VendorReviews: React.FC = () => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<GraphicsVendorReview[]>([]);
  const [stats, setStats] = useState<ReviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [vendorProfile, setVendorProfile] = useState<any>(null);

  useEffect(() => {
    fetchVendorProfile();
  }, [user?.id]);

  useEffect(() => {
    if (vendorProfile) {
      fetchReviews();
      fetchStats();
    }
  }, [vendorProfile]);

  const fetchVendorProfile = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('graphics_vendor_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      setVendorProfile(data);
    } catch (error) {
      console.error('Error fetching vendor profile:', error);
      toast.error('Failed to load vendor profile');
    }
  };

  const fetchReviews = async () => {
    if (!vendorProfile?.id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('graphics_vendor_reviews')
        .select(`
          *,
          profiles!graphics_vendor_reviews_reviewer_id_fkey(name, email),
          graphics_orders!graphics_vendor_reviews_order_id_fkey(
            total_amount,
            graphics_packages!graphics_orders_package_id_fkey(name)
          )
        `)
        .eq('vendor_id', vendorProfile.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!vendorProfile?.id) return;

    try {
      const { data, error } = await supabase
        .from('graphics_vendor_reviews')
        .select('rating, created_at')
        .eq('vendor_id', vendorProfile.id);

      if (error) throw error;

      const totalReviews = data?.length || 0;
      const averageRating = totalReviews > 0 
        ? data.reduce((sum, review) => sum + review.rating, 0) / totalReviews 
        : 0;

      const ratingDistribution = [1, 2, 3, 4, 5].reduce((acc, rating) => {
        acc[rating] = data?.filter(review => review.rating === rating).length || 0;
        return acc;
      }, {} as { [key: number]: number });

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const recentReviews = data?.filter(review => 
        new Date(review.created_at) > thirtyDaysAgo
      ).length || 0;

      setStats({
        total_reviews: totalReviews,
        average_rating: averageRating,
        rating_distribution: ratingDistribution,
        recent_reviews: recentReviews,
      });
    } catch (error) {
      console.error('Error fetching review stats:', error);
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`h-4 w-4 ${
          index < rating
            ? 'fill-yellow-400 text-yellow-400'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin mr-2" />
        <span>Loading reviews...</span>
      </div>
    );
  }

  if (!vendorProfile) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <User className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Vendor Profile</h3>
            <p className="text-muted-foreground text-center">
              You need to set up your vendor profile before viewing reviews.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Reviews & Ratings</h1>
        <p className="text-muted-foreground">
          Manage and view customer feedback for your graphics services
        </p>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Reviews</CardTitle>
              <MessageCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_reviews}</div>
              <p className="text-xs text-muted-foreground">
                All time customer reviews
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Average Rating</CardTitle>
              <Star className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold flex items-center gap-2">
                {stats.average_rating.toFixed(1)}
                <div className="flex">
                  {renderStars(Math.round(stats.average_rating))}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Based on {stats.total_reviews} reviews
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Recent Reviews</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.recent_reviews}</div>
              <p className="text-xs text-muted-foreground">
                Last 30 days
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Top Rating</CardTitle>
              <Trophy className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.rating_distribution[5] || 0}</div>
              <p className="text-xs text-muted-foreground">
                5-star reviews
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Rating Distribution */}
      {stats && stats.total_reviews > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Rating Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[5, 4, 3, 2, 1].map((rating) => {
                const count = stats.rating_distribution[rating] || 0;
                const percentage = stats.total_reviews > 0 
                  ? (count / stats.total_reviews) * 100 
                  : 0;
                
                return (
                  <div key={rating} className="flex items-center gap-2">
                    <div className="flex items-center gap-1 w-16">
                      <span className="text-sm font-medium">{rating}</span>
                      <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                    </div>
                    <div className="flex-1 bg-muted rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-sm text-muted-foreground w-8">
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reviews List */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Customer Reviews</h2>
        
        {reviews.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <MessageCircle className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Reviews Yet</h3>
              <p className="text-muted-foreground text-center">
                Complete your first order to start receiving customer reviews.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {reviews.map((review) => (
              <Card key={review.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="flex">
                          {renderStars(review.rating)}
                        </div>
                        <Badge variant="secondary">
                          Review
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <User className="h-4 w-4" />
                        <span>{review.profiles?.name || 'Anonymous Customer'}</span>
                        <Calendar className="h-4 w-4 ml-2" />
                        <span>{formatDate(review.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="pt-0">
                  {review.review_text && (
                    <p className="text-sm mb-3">{review.review_text}</p>
                  )}
                  
                  {review.graphics_orders && (
                    <div className="bg-muted/50 rounded-lg p-3">
                      <div className="text-xs text-muted-foreground mb-1">Order Details</div>
                       <div className="text-sm font-medium">
                         {review.graphics_orders.graphics_packages?.name || 'Custom Package'}
                       </div>
                      {review.graphics_orders.total_amount && (
                        <div className="text-xs text-muted-foreground">
                          Order Value: ${review.graphics_orders.total_amount}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorReviews;